<template>
	<v-card class="resource-card-shell">
		<template v-if="gatewayData">
			<div class="entity-card">
				<!-- Header: icon + name -->
				<div class="entity-card__header">
					<span
						class="data-source-icon data-source-icon--gateway data-source-icon--32"
						role="img"
						aria-label="Sensor Gateway"
					/>
					<div class="sgnl-flex sgnl-align-center sgnl-gap-4">
						<v-tooltip
							bottom
							:allow-overflow="true"
						>
							<template v-slot:activator="{ props }">
								<span
									class="sgnl-paragraph-m-medium sgnl-text-color-default"
									v-bind="props"
									>{{ gatewayData.Name }}</span
								>
							</template>
							<div>
								Gateway Local IP Address: {{ gatewayIPAddress }}<br />
								Unique Source ID: {{ gatewayData.ThingID }}
							</div>
						</v-tooltip>
						<v-tooltip bottom>
							<template v-slot:activator="{ props }">
								<v-btn
									icon
									variant="text"
									size="x-small"
									v-bind="props"
									@click="showEditNameDialog = true"
								>
									<Edit16 />
								</v-btn>
							</template>
							<span>Edit Gateway Name</span>
						</v-tooltip>
					</div>
				</div>

				<div class="entity-card__body">
					<!-- Badge: IP address + status -->
					<div class="sgnl-flex sgnl-align-center sgnl-gap-8">
						<span class="gateway-badge sgnl-text-color-secondary">
							<NetworkInterface16 />
							{{ gatewayIPAddress }}
						</span>
						<template v-if="gatewayStatusLoading">
							<v-progress-circular
								:size="16"
								class="sgnl-icon-color-brand"
							/>
						</template>
						<template v-else>
							<v-tooltip open-delay="500">
								<template v-slot:activator="{ props }">
									<span
										class="gateway-badge sgnl-text-color-secondary"
										:class="'gateway-badge--' + gatewayStatus.toLowerCase()"
										v-bind="props"
									>
										<span
											class="status-dot"
											:style="{ backgroundColor: gatewayStatusColor }"
										/>
										{{ gatewayStatus }}
									</span>
								</template>
								<span>{{ gatewayStatus }}<br />Last active - {{ gatewayLastUpdatedString }}</span>
							</v-tooltip>
						</template>
					</div>

					<!-- Metadata -->
					<div class="sgnl-paragraph-s-regular entity-card__meta sgnl-text-color-secondary">
						<div
							v-if="!$vuetify.display.mobile"
							class="sgnl-flex sgnl-align-center sgnl-gap-4"
						>
							<AddAlt16 class="sgnl-icon-color-secondary" />Created:
							{{ formatDate(gatewayData.CreatedAt) }}
						</div>
						<div class="sgnl-flex sgnl-align-center sgnl-gap-4">
							<Renew16 class="sgnl-icon-color-secondary" />Last Updated:
							{{ formatDate(gatewayData.UpdatedAt) }}
						</div>
					</div>
				</div>

				<!-- Actions -->
				<div class="entity-card__actions">
					<v-btn
						variant="flat"
						color="primary"
						@click="showGatewayDataStreamsDialog = true"
					>
						<Archive16 />
						<span>View Data Stream</span>
					</v-btn>
					<v-btn
						variant="outlined"
						:disabled="!gatewayIPKnown"
						:href="`http://${gatewayIPAddress}:5000`"
						target="_blank"
					>
						<Settings16 />
						<span>Configure Gateway</span>
						<Launch16 />
					</v-btn>
				</div>
			</div>
		</template>
		<DataSourceCardSkeleton
			v-else
			:trailingAction="false"
		>
			<template #pills>
				<SkeletonBlock
					width="110px"
					height="26px"
					radius="var(--sgnl-border-radius-16)"
				/>
				<SkeletonBlock
					width="70px"
					height="26px"
					radius="var(--sgnl-border-radius-16)"
				/>
			</template>
			<template #text>
				<SkeletonBlock
					width="55%"
					height="var(--sgnl-unit-14)"
					radius="var(--sgnl-border-radius-6)"
				/>
				<SkeletonBlock
					width="45%"
					height="var(--sgnl-unit-14)"
					radius="var(--sgnl-border-radius-6)"
				/>
			</template>
			<template #actions>
				<SkeletonBlock
					width="140px"
					btn
				/>
				<SkeletonBlock
					width="150px"
					btn
				/>
			</template>
		</DataSourceCardSkeleton>
		<EditValueDialog
			v-if="showEditNameDialog"
			title="Set Gateway Name"
			valueHint="Gateway Name"
			v-model:showDialog="showEditNameDialog"
			:value="gatewayData.Name"
			@update:value="setGatewayName"
		/>
		<GatewayDataStreamsDialog
			v-if="showGatewayDataStreamsDialog"
			v-model:showDialog="showGatewayDataStreamsDialog"
			:gatewayID="gatewayID"
		/>
	</v-card>
</template>

<script>
import { defineComponent } from "vue";
import Edit16 from "@carbon/icons-vue/es/edit/16";
import Settings16 from "@carbon/icons-vue/es/settings/16";
import Launch16 from "@carbon/icons-vue/es/launch/16";
import NetworkInterface16 from "@carbon/icons-vue/es/network-interface/16";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import Archive16 from "@carbon/icons-vue/es/archive/16";
import AddAlt16 from "@carbon/icons-vue/es/add--alt/16";
import { sdk } from "@/js/sdk";
import EditValueDialog from "@/components/DataSources/EditValueDialog.vue";
import GatewayDataStreamsDialog from "@/components/DataSources/GatewayDataStreamsDialog.vue";
import DataSourceCardSkeleton from "@/components/DataSources/DataSourceCardSkeleton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import * as dsUtil from "@/components/DataSources/utilities";

export default defineComponent({
	name: "GatewayCard",
	components: {
		GatewayDataStreamsDialog,
		EditValueDialog,
		DataSourceCardSkeleton,
		SkeletonBlock,
		Edit16,
		Settings16,
		Launch16,
		NetworkInterface16,
		Renew16,
		AddAlt16,
		Archive16,
	},
	props: {
		gatewayID: {
			type: String,
			default: "",
		},
	},
	data: () => ({
		gatewayData: undefined,
		showEditNameDialog: false,
		showGatewayDataStreamsDialog: false,
		gatewayStatus: "Disconnected",
		gatewayStatusIcon: {},
		gatewayStatusLoading: false,
		gatewayLastUpdated: "",
	}),
	methods: {
		formatDate(dtString) {
			return dsUtil.formatDateDayString(1000 * dtString);
		},
		getGatewayInfo() {
			return sdk.things
				.getOne(this.gatewayID)
				.then((response) => {
					this.gatewayData = response;
					this.getGatewayStatus();
				})
				.catch((error) => console.log(error));
		},

		// * setters
		async setGatewayName(newGatewayName) {
			sdk.things
				.update(this.gatewayID, { Name: newGatewayName })
				.then(() => {
					this.getGatewayInfo();
				})
				.catch((error) => console.log(error));
		},
		async getGatewayStatus() {
			this.gatewayStatusLoading = true;
			this.gatewayLastUpdated = await dsUtil.gatewayLastUpdated(this.gatewayData);
			this.gatewayStatus = dsUtil.gatewayStatusFromLastUpdated(this.gatewayLastUpdated);
			this.gatewayStatusIcon = dsUtil.gatewayStatusIcon(this.gatewayStatus);
			if (this.gatewayStatusIcon) {
				this.gatewayStatusLoading = false;
			}
		},
		dataSourceIcon(dataSourceType) {
			return dsUtil.dataSourceIcon(dataSourceType);
		},
	},
	computed: {
		gatewayIPAddress() {
			return dsUtil.gatewayIPAddressString(this.gatewayData);
		},
		gatewayIPKnown() {
			return dsUtil.gatewayIPKnown(this.gatewayData);
		},
		gatewayLastUpdatedString() {
			return dsUtil.formatDateDayString(1000 * this.gatewayLastUpdated);
		},
		gatewayStatusColor() {
			switch (this.gatewayStatus) {
				case "Connected":
					return "var(--sgnl-alert-success-icon)";
				case "Disconnected":
					return "var(--sgnl-alert-error-icon)";
				default:
					return "var(--sgnl-alert-warning-icon)";
			}
		},
	},
	mounted() {
		this.getGatewayInfo();
	},
});
</script>

<style lang="scss" scoped>
.status-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
}

.gateway-badge {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-4);
	padding: var(--sgnl-space-2) var(--sgnl-space-10);
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	background: var(--sgnl-default-primary-background-mono);
	font-size: var(--sgnl-font-size-caption);
	line-height: 1.5;

	&--connected {
		border-color: var(--sgnl-alert-success-border);
		background: var(--sgnl-alert-success-background-primary);
		color: var(--sgnl-alert-success-text);
	}

	&--disconnected {
		border-color: var(--sgnl-alert-error-border);
		background: var(--sgnl-alert-error-background-primary);
		color: var(--sgnl-alert-error-text);
	}

	&--stale {
		border-color: var(--sgnl-alert-warning-border);
		background: var(--sgnl-alert-warning-background-primary);
		color: var(--sgnl-alert-warning-text);
	}
}
</style>
